import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { ChevronDown, ChevronRight, ExternalLink, Check, Clock, AlertTriangle, MessageSquare, FileText, Loader2, RefreshCw } from "lucide-react";
import { getSettings, getLeads, saveLeads } from "./demoConfig";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

const OBJECTION_STYLES = {
  price: { bg: "#FAECE7", color: "#712B13", label: "Price objection" },
  timing: { bg: "#FAEEDA", color: "#633806", label: "Timing / not ready" },
  trust: { bg: "#E6F1FB", color: "#0C447C", label: "Trust / proof" },
  diy: { bg: "#EEEDFE", color: "#3C3489", label: "DIY objection" },
  competitor: { bg: "#F1EFE8", color: "#444441", label: "Comparing options" },
};

export default function CallIntelligence({ lead, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);

  const hasFathomData = !!lead.fathomSyncedAt;
  const settings = getSettings();

  const toggleActionItem = (idx) => {
    const items = [...(lead.fathomActionItems || [])];
    items[idx] = { ...items[idx], completed: !items[idx].completed };
    const leads = getLeads();
    const updated = leads.map((l) => l.id === lead.id ? { ...l, fathomActionItems: items } : l);
    saveLeads(updated);
    if (onUpdate) onUpdate();
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const resp = await fetch(`${API}/api/fathom-refresh/${lead.id}`, { method: 'POST' });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        setRefreshMsg(err.detail || `Failed (${resp.status}) — check Fathom API key in Settings`);
        return;
      }
      const data = await resp.json();
      if (!data.synced) {
        setRefreshMsg(data.message || "No call found yet");
        return;
      }
      setRefreshMsg("Updated");
      setTimeout(() => setRefreshMsg(null), 2000);
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error("Fathom refresh failed:", e);
      setRefreshMsg("Network error — try again");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="ci-section" data-testid={`call-intel-${lead.id}`}>
      <button className="ci-toggle" onClick={() => setOpen(!open)} data-testid={`ci-toggle-${lead.id}`}>
        <Mic2Icon />
        <span>Call intelligence</span>
        {hasFathomData && <span className="ci-synced-dot" />}
        {open ? <ChevronDown className="w-3.5 h-3.5 ml-auto" /> : <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
      </button>

      {open && !hasFathomData && (
        <div className="ci-empty" data-testid={`ci-empty-${lead.id}`}>
          <p>Waiting for Fathom...</p>
          <p>Call data appears automatically 2–3 minutes after the call ends.</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            <button className="ci-refresh-btn" onClick={handleRefresh} disabled={refreshing} data-testid={`ci-refresh-${lead.id}`}>
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {refreshing ? "Checking..." : "Refresh from Fathom"}
            </button>
            <button
              className="ci-refresh-btn"
              data-testid={`ci-poll-${lead.id}`}
              onClick={async () => {
                setRefreshing(true);
                setRefreshMsg(null);
                try {
                  const API = process.env.REACT_APP_BACKEND_URL || '';
                  const r = await fetch(`${API}/api/fathom-poll?hours=24`, { method: 'POST' });
                  if (!r.ok) {
                    const e = await r.json().catch(() => ({}));
                    setRefreshMsg(e.detail || `Poll failed (${r.status})`);
                    return;
                  }
                  const d = await r.json();
                  setRefreshMsg(`Scanned ${d.scanned}, matched ${d.matched} lead(s)`);
                  if (onUpdate) onUpdate();
                } catch (e) {
                  setRefreshMsg("Network error");
                } finally { setRefreshing(false); }
              }}
              disabled={refreshing}
              title="Pull recent Fathom calls + auto-match to all leads (admin)"
            >
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Pull last 24h
            </button>
          </div>
          {refreshMsg && <span className="ci-refresh-msg">{refreshMsg}</span>}
        </div>
      )}

      {open && hasFathomData && (
        <div className="ci-content" data-testid={`ci-content-${lead.id}`}>
          {/* Stats bar */}
          <div className="ci-stats-bar">
            <span className={`ci-duration ci-dur-${getDurColor(lead.callDurationMinutes)}`}>
              <Clock className="w-3 h-3" /> {lead.callDurationMinutes} min
            </span>
            {lead.durationFlag === 'short' && <span className="ci-flag ci-flag-red">Short call — review</span>}
            {lead.durationFlag === 'long' && <span className="ci-flag ci-flag-amber">Long call — review recording</span>}
            {lead.fathomRecordingUrl && (
              <a href={lead.fathomRecordingUrl} target="_blank" rel="noopener noreferrer" className="ci-recording-link" data-testid={`ci-recording-${lead.id}`}>
                <ExternalLink className="w-3 h-3" /> Watch recording
              </a>
            )}
          </div>

          {/* Summary */}
          {lead.fathomSummary && (
            <div className="ci-block">
              <span className="ci-block-label">Fathom summary</span>
              <div className={`ci-summary-text ${summaryExpanded ? "ci-summary-expanded" : "ci-summary-collapsed"}`}>
                {lead.fathomSummary}
              </div>
              <button className="ci-expand-btn" onClick={() => setSummaryExpanded(!summaryExpanded)}>
                {summaryExpanded ? "Show less" : "Show more"}
              </button>
            </div>
          )}

          {/* Action Items */}
          <div className="ci-block">
            <span className="ci-block-label">Action items</span>
            {(!lead.fathomActionItems || lead.fathomActionItems.length === 0) ? (
              <p className="ci-muted">No action items detected</p>
            ) : (
              <div className="ci-action-list">
                {lead.fathomActionItems.map((item, idx) => (
                  <div key={`ai-${idx}`} className="ci-action-item">
                    <button className={`ci-checkbox ${item.completed ? "ci-checked" : ""}`} onClick={() => toggleActionItem(idx)} data-testid={`ci-action-${lead.id}-${idx}`}>
                      {item.completed && <Check className="w-3 h-3" />}
                    </button>
                    <span className={item.completed ? "ci-action-done" : ""}>{item.description}</span>
                    {item.playbackUrl && <a href={item.playbackUrl} target="_blank" rel="noopener noreferrer" className="ci-playback-link"><ExternalLink className="w-3 h-3" /></a>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Objection Tags */}
          <div className="ci-block">
            <span className="ci-block-label">Objections detected</span>
            {(!lead.objectionTags || lead.objectionTags.length === 0) ? (
              <p className="ci-muted">No objections detected</p>
            ) : (
              <div className="ci-tag-row">
                {lead.objectionTags.map((tag) => {
                  const style = OBJECTION_STYLES[tag];
                  return style ? (
                    <span key={tag} className="ci-tag" style={{ background: style.bg, color: style.color }}>{style.label}</span>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Transcript */}
          {lead.fathomTranscript && lead.fathomTranscript.length > 0 && (
            <div className="ci-block">
              <button className="ci-expand-btn" onClick={() => setTranscriptOpen(!transcriptOpen)}>
                {transcriptOpen ? "Hide transcript" : "View transcript"}
              </button>
              {transcriptOpen && (
                <div className="ci-transcript" data-testid={`ci-transcript-${lead.id}`}>
                  {lead.fathomTranscript.map((t, i) => (
                    <div key={`tr-${i}`} className={`ci-tr-row ${i % 2 === 0 ? "ci-tr-even" : ""}`}>
                      <div className="ci-tr-header">
                        <span className="ci-tr-speaker">{t.speaker}</span>
                        <span className="ci-tr-time">{t.timestamp}</span>
                      </div>
                      <p className="ci-tr-text">{t.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Refresh button */}
          {settings.fathomApiKey && (
            <div className="ci-refresh-row">
              <button className="ci-refresh-btn" onClick={handleRefresh} disabled={refreshing}>
                {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {refreshing ? "Refreshing..." : "Refresh from Fathom"}
              </button>
              {refreshMsg && <span className="ci-refresh-msg">{refreshMsg}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Mic2Icon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>;
}

function getDurColor(mins) {
  if (!mins) return 'gray';
  if (mins < 8) return 'red';
  if (mins > 20) return 'amber';
  return 'green';
}
